import { useContext, useState } from "react";
import type { Pojo } from "../app-patient/_objects-patient";
import { icons, type2Title, type BusinessType } from "../config";
import { RequestContext } from "../request-context";
import { date2HumanString, normalizeDate } from "../utils/date";
import { requestBodyJson, requestPost, requireResult } from "../utils/network";
import { passThrough } from "../utils/promises";
import ActionButton from "./action-button";
import ActionConfirm from "./action-confirm";
import ButtonsGroup from "./buttons-group";
import FormValidating from "./form-validating";
import { EditContext } from "./io-abstract";
import notification from "./notification";
import Panel from "./panel";

export default function IOPanel<T extends Pojo>(props: {
  apiRootUrl: string;
  type: BusinessType;
  file: T;
  header?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  closed?: boolean;
  edit?: boolean;
  canBeLocked?: boolean;
  isProtected?: boolean;
  restrictedForWrite: string;
  restrictedForUnlock?: string;
  prepareData?: (file: FormData) => FormData | Record<string, unknown>;
  validateData?: (data: object) => string[];
  onCreated: (file: T) => void;
  onDeleted: (file: T) => void;
  onUpdated: (file: T) => void;
  onEdit?: (edit: boolean) => void;
}): React.ReactNode {
  const requestContext = useContext(RequestContext)!;
  const [edit, setEditState] = useState<boolean>(
    !props.file.id || (props.edit ?? false)
  );
  const setEdit = (newEdit: boolean) => {
    if (newEdit != edit) {
      if (props.onEdit) props.onEdit(newEdit);
      setEditState(newEdit);
    }
  };
  const [formErrors, setFormErrors] = useState<string[]>([]);

  const title = type2Title(props.type);

  let isLocked = false;
  if (props.canBeLocked) {
    if (props.file.updated_at) {
      const deadline = new Date(props.file.updated_at);
      deadline.setDate(deadline.getDate() + 35);
      isLocked = deadline < new Date();
    }
  }

  const prepareData = (
    formData: FormData
  ): FormData | Record<string, unknown> => {
    if (props.prepareData) {
      return props.prepareData(formData);
    }

    return formData;
  };

  const canDelete = !props.isProtected && !!props.file.id;

  const doCancel = () => {
    setEdit(false);
    props.onEdit?.(false);
  };

  const doSave = (formData: FormData) => {
    if (!props.file.id) {
      return (
        requestContext
          .fetchJSONAndMessage<T>(
            "Creating file...",
            requestPost(props.apiRootUrl, prepareData(formData))
          )
          .then(requireResult)
          .then(notification("File created"))
          .then(passThrough(() => setEdit(false)))
          .then((file) => ({ ...file, _type: props.type }) as T)
          // Propagate data to parent before leaving edit mode.
          .then(passThrough(props.onCreated))
      );
    } else {
      return (
        requestContext
          .fetchJSONAndMessage<T>(
            "Saving file...",
            requestBodyJson(
              `${props.apiRootUrl}/${props.file.id}`,
              "PUT",
              prepareData(formData)
            )
          )
          .then(requireResult)
          .then((file) => ({ ...file, _type: props.type }))
          // Propagate data to parent before leaving edit mode.
          .then(passThrough(props.onUpdated))
          .then(notification("File updated"))
          .then(passThrough(() => setEdit(false)))
      );
    }
  };

  const doUnlock = () => {
    requestContext
      .fetchJSONAndMessage(
        "Unlocking file...",
        requestBodyJson(`${props.apiRootUrl}/unlock/${props.file.id}`, "PUT")
      )
      .then((json) => ({ ...(json as T), _type: props.type }))
      // Propagate updated data to parent before leaving edit mode.
      .then(passThrough(props.onUpdated))
      .then(notification("File unlocked"))
      .then(passThrough(() => setEdit(true)));
  };

  const doDelete = () => {
    requestContext
      .fetchJSONAndMessage<void>(
        "Deleting file...",
        requestBodyJson(`${props.apiRootUrl}/${props.file.id}`, "DELETE")
      )
      .then(notification("File deleted"))
      .then(passThrough(() => setEdit(false)))
      .then(passThrough(() => props.onDeleted(props.file)));
  };

  return (
    <Panel
      testid={`${props.type}.${props.file.id ?? "add"}`}
      closed={props.closed}
      header={
        <>
          <span className="first" data-mode={edit ? "input" : "output"}>
            <img
              src={
                icons.models[(props.type as keyof typeof icons.models) ?? ""]
              }
              alt={type2Title(props.type)}
              className="inline"
            />
            {"date" in props.file ? (
              <span className="no-mobile">
                {date2HumanString(normalizeDate(props.file["date"] as string))}
              </span>
            ) : null}
            <span data-role="type" className="no-mobile">
              {type2Title(props.type)}
            </span>
          </span>
          {props.header}
        </>
      }
    >
      <FormValidating
        action={doSave}
        validate={props.validateData}
        confirmNavigation={true}
        onValidationErrors={(errors) => setFormErrors(errors)}
      >
        <div className="d-flex justify-content-between">
          <div className="technical" data-e2e="excluded">
            {`${props.type}.${props.file.id ?? "add"}`}
          </div>
          <ButtonsGroup data-testid="panel-actions">
            {edit ? (
              <>
                {canDelete && (
                  <ActionConfirm
                    style="Delete"
                    discrete={true}
                    onOk={doDelete}
                    restrictedTo={props.restrictedForWrite}
                  >
                    <div>Are you sure you want to DELETE the File {title}?</div>
                  </ActionConfirm>
                )}
                <ActionButton style="Cancel" onOk={() => doCancel()} />
                <ActionButton
                  action={props.file.id ? "Save" : "Create"}
                  disabled={formErrors.length > 0}
                  restrictedTo={props.restrictedForWrite}
                  style="Confirm"
                  type="submit"
                />
              </>
            ) : isLocked ? (
              <ActionConfirm
                style="Alternate"
                action="Unlock"
                discrete={true}
                onOk={() => doUnlock()}
                restrictedTo={props.restrictedForUnlock}
              >
                <div>
                  Are you sure you want to unlock the File {title}?
                  <br />
                  Anybody will then be able to edit it.
                </div>
              </ActionConfirm>
            ) : (
              <ActionButton
                style="Edit"
                onOk={() => setEdit(true)}
                restrictedTo={props.restrictedForWrite}
              />
            )}
          </ButtonsGroup>
        </div>
        <EditContext.Provider value={edit}>
          {props.file.updated_at && (
            <input
              type="hidden"
              name="updated_at"
              value={props.file.updated_at}
            />
          )}
          {edit &&
            formErrors.length > 0 &&
            formErrors.map((error, index) => (
              <div key={index} className="alert alert-danger" role="alert">
                {error}
              </div>
            ))}
          {props.children}
          {edit && (
            <ButtonsGroup align="center">
              <ActionButton style="Cancel" onOk={() => doCancel()} />
              <ActionButton
                action={props.file.id ? "Save" : "Create"}
                disabled={formErrors.length > 0}
                restrictedTo={props.restrictedForWrite}
                style="Confirm"
                type="submit"
              />
            </ButtonsGroup>
          )}
        </EditContext.Provider>
      </FormValidating>
      {props.footer}
    </Panel>
  );
}
